import { FC, useEffect } from 'react';

import { nativeHooks } from '@bluecodecom/native';

import { trackingService } from '../../services';

/**
 * Registers the SDK telemetry session id from the native host config as the
 * `session_id` super property, and re-registers if the host config delivers a
 * new one. Must run inside `NativeProviders.VasHostConfig`.
 *
 * Nothing is registered while the id is unknown (`null`) — older SDK builds
 * that do not send `sdkSessionId` yet simply produce events without it.
 *
 * **Do not add new mounts of this.** `trackingService.init` now installs its
 * own host-config handler and registers the same property in every webview
 * (see `services/tracking/sdkSession.ts`), which is what a property the catalog
 * wants on *every* event should have been from the start: this component
 * reached four of the seven minis that mount the provider, and the other three
 * mounted the provider while registering nothing. The four existing mounts are
 * left in place deliberately — they register the identical value, and they are
 * the path verified on a device — but they are redundant, and this is the half
 * of the pair to delete once the central one has been confirmed on the
 * non-mini containers too (NAT-151).
 */
export const useSdkSessionSuperProperty = () => {
  const { sdkSessionId } = nativeHooks.useVasHostConfigContext();

  useEffect(() => {
    trackingService.registerSdkSession(sdkSessionId);
  }, [sdkSessionId]);
};

/**
 * Drop-in for apps that have no component of their own below the host-config
 * provider: renders nothing, runs {@link useSdkSessionSuperProperty}.
 */
export const SdkSessionSuperProperty: FC = () => {
  useSdkSessionSuperProperty();

  return null;
};

export default useSdkSessionSuperProperty;
